import { Component, Input } from '@angular/core';
import * as constants from '../../../constants-types/constants';
import { ODataServiceBase } from '../../../core-services/odata/odata-base.service';
import {
    ODataPageInfo,
    ODataSearch,
    ODataSearchState
} from '../../../core-services/odata/odata.models';
import { Util } from '../../../core-utilities/Util';
import { TranslationService } from '../../../translations/Translation.service';
import { PagerUtilities } from '../../Pager.utility';
@Component({
    selector: 'odata-pager',
    template: `
        @if (util.hasValue(searchState?.searchResults?.totalRecords)) {
            <div class="pi-pagination">
                <div
                    data-testing="pagingAndTotalCountInformation"
                    class="pi-pagination_resultSizeInformation"
                >
                    {{ resultSizeInformation() }}
                </div>
                <div>
                    <span
                        class="pi-pagination_pageNumber"
                        [ngClass]="{
                            'pi-pagination_pageNumber--clickableNumber':
                                1 < pageInfo.currentPage
                        }"
                        (click)="goToPage(pageInfo.currentPage - 1)"
                    >
                        {{ TR.key('text.common-terms.prev') }}
                    </span>
                    @for (
                        pageNumber of pagerUtil.pagingNumbersDisplay(
                            pageInfo.currentPage,
                            getMaxPages
                        );
                        track pageNumber
                    ) {
                        <span
                            class="pi-pagination_pageNumber"
                            [ngClass]="{
                                'pi-pagination_pageNumber--clickableNumber':
                                    isClickablePage(pageNumber),
                                'pi-pagination_pageNumber--currentPage':
                                    isCurrentPage(pageNumber)
                            }"
                            (click)="goToPage(pageNumber)"
                        >
                            @if (pageNumber === ellipsisValue) {
                                <span>...</span>
                            } @else {
                                {{ pageNumber }}
                            }
                        </span>
                    }
                    <span
                        class="pi-pagination_pageNumber"
                        [ngClass]="{
                            'pi-pagination_pageNumber--clickableNumber':
                                searchState?.searchResults?.totalRecords /
                                    pageInfo.resultsPerPage >
                                pageInfo.currentPage
                        }"
                        (click)="goToPage(pageInfo.currentPage + 1)"
                    >
                        {{ TR.key('text.common-terms.next') }}
                    </span>
                </div>
                <odata-pager-size-picker
                    [searchState]="searchState"
                    (onResultsSizeChange)="updateResultsPerPage($event)"
                >
                </odata-pager-size-picker>
            </div>
        }
    `,
    standalone: false
})
export class ODataPagerComponent {
    @Input()
    oDataService: ODataServiceBase<any>;

    resultsPerPageOptions = [
        constants.tableDefaultResultsPerPage,
        constants.serverApiMaxResults
    ];

    // To display the "..."
    ellipsisValue = -1;

    constructor(
        public TR: TranslationService,
        public util: Util,
        public pagerUtil: PagerUtilities
    ) {}
    // use any here because we aren't referencing the result value
    get searchState(): ODataSearchState<any> {
        return this.oDataService.state;
    }
    get searchQuery(): ODataSearch {
        return this.searchState?.oDataSearch;
    }

    get pageInfo(): ODataPageInfo {
        return this.searchQuery?.pageInfo;
    }

    get pagingData() {
        return this.searchState.searchResults?.records;
    }

    get getMaxPages() {
        return Math.ceil(
            this.searchState.searchResults.totalRecords /
                this.pageInfo.resultsPerPage
        );
    }

    isClickablePage(pageNumber: number): boolean {
        return (
            pageNumber !== this.pageInfo.currentPage &&
            pageNumber !== this.ellipsisValue
        );
    }

    isCurrentPage(pageNumber: number): boolean {
        return pageNumber === this.pageInfo.currentPage;
    }

    resultSizeInformation() {
        if (this.searchState.searchResults.totalRecords === 0) {
            return this.TR.key('tablePager.noResultsFound');
        }

        let currentResultRange = `${
            (this.pageInfo.currentPage - 1) * this.pageInfo.resultsPerPage + 1
        }-`;
        if (
            this.pageInfo.currentPage * this.pageInfo.resultsPerPage >
            this.searchState.searchResults.totalRecords
        ) {
            currentResultRange += this.searchState.searchResults.totalRecords;
        } else {
            currentResultRange += `${
                this.pageInfo.currentPage * this.pageInfo.resultsPerPage
            }`;
        }

        return this.util.strings.formatString(
            this.TR.key('tablePager.resultSize'),
            currentResultRange,
            this.searchState.searchResults.totalRecords
        );
    }

    async updateResultsPerPage(resultsPerPage: number) {
        await this.oDataService
            .setPageInfo({
                currentPage: 1,
                resultsPerPage
            })
            .getResults();
    }

    async goToPage(pageNumber: number) {
        // Don't bother
        if (
            pageNumber === this.pageInfo.currentPage ||
            // Don't go past bounds
            pageNumber < 1 ||
            pageNumber > this.getMaxPages
        ) {
            return;
        }

        await this.oDataService.goToPage(pageNumber).getResults();
    }
}
